import { useEffect, useState } from "react"
import Header from "./Header"
import Sidebar from "./Sidebar"
import AnimatedOutlet from "./AnimatedOutlet"
import { TourProvider, TourOverlay } from "../tour"
import { useMeetingReminders } from "@/features/meetings/hooks/useMeetingReminders"
import { useConversationList } from "@/features/messenger/hooks/useConversationList"
import { useLocalChatSync } from "@/features/messenger/hooks/useLocalChatSync"
import { useContactRegistration } from "@/features/messenger/hooks/useContactRegistration"
import { useIncomingMessageAlerts } from "@/features/messenger/hooks/useIncomingMessageAlerts"
import { useRemapUnmappedConversations } from "@/features/messenger/hooks/useRemapUnmappedConversations"

export default function DashboardLayout() {
  // Polls the meetings cache and emits a reminder notification when each
  // upcoming meeting enters its configured reminder window.
  useMeetingReminders()

  // Resolve the per-org `contactPresenceId` (into `useAuthStore.presenceId`)
  // SILENTLY on every dashboard page, so chat + presence (guarded on `presenceId`)
  // work for already-consented users everywhere. This shows NO UI — the terms
  // gate for un-consented users lives on the chat tab (dismissible), so chat
  // (which is optional) never blocks meetings/scheduling.
  useContactRegistration()

  // NOTE: the own `user:<presenceId>` topic (presence + conversation:added /
  // conversation:activity) is owned by `useOwnUserChannel()` in ProtectedRoute —
  // a SINGLE channel, app-wide. It used to live here as a second channel, which
  // collided with the presence subscription (Phoenix closes duplicate joins on
  // the same topic) and silently killed the live-new-chat listener.

  // Local-first chat cache: hydrate from IndexedDB on login (instant, offline),
  // write-through persist, and delta-sync what changed while away. Mounted
  // before the list subscription so cached state renders immediately.
  useLocalChatSync()

  // App-wide chat presence: hydrate the conversation list AND join every
  // conversation channel for as long as the user is logged in — not just on
  // /chats. This lets a recipient sitting on /meetings still RECEIVE messages
  // (so the sender sees ✓✓ delivered), and keeps unread counts + the sidebar
  // badge live on every page.
  useConversationList()

  // A conversation can come back from the list without my own participant
  // row in it (stale/missing mapping between presenceId and that thread):
  // the unread badge shows nothing for one of these rather than guessing,
  // but the real fix is re-fetching it so the server's row (and lastReadAt)
  // comes back. Mounted here for the same app-wide-regardless-of-page reason
  // as useConversationList.
  useRemapUnmappedConversations()

  // Web has no notification of its own for an incoming group/
  // direct message: the server skips Firebase push to a device it already
  // sees as online, so a tab left open (on ANY page, not just /chats) showed
  // nothing. Mounted here for the same reason useConversationList is: so it
  // keeps seeing live messages regardless of which page is on screen.
  useIncomingMessageAlerts()

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true
    return window.innerWidth >= 1024
  })

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true)
      } else {
        setIsSidebarOpen(false)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // const { name: moduleName, description } = getModuleConfig()
// [#F7F7F7]
  return (
    <TourProvider>
    <div className="flex h-[100dvh] w-screen bg-[#F2F2F2] dark:bg-[#212121] overflow-hidden">
      <div className="flex w-full h-full">
     
      {!isSidebarOpen && (
        <button
          data-sidebar
          onClick={() => setIsSidebarOpen(true)}
          className="hidden lg:block fixed top-4 left-4 z-30 p-3 bg-primary rounded-xl shadow-lg animate-fade-in"
          aria-label="Open sidebar"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}
      <div data-sidebar className="landscape:pl-2 py-2">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
      </div>

      <div className="flex flex-1 h-full flex-col transition-all duration-300 overflow-hidden">
        <div data-header>
          <Header
            onMenuClick={() => setIsSidebarOpen(true)}
          />
        </div>
        {/* <SubHeader 
          moduleName={moduleName}
          description={description}
          OptionalAction={moduleName === "Meetings" ? true : false}
        /> */}
        <AnimatedOutlet />
      </div>
      </div>
    </div>
    <TourOverlay />
    {/* The terms gate is NOT shown app-wide — chat is optional and must never
        block meetings/scheduling. It lives on the chat tab (PresencePage),
        dismissible. Here we only resolve `presenceId` silently (above). */}
    </TourProvider>
  )
}
